import React, { useCallback, useEffect, useState } from "react";

import { getAllFields, getDigifarmMapsGeo, getFieldShapeAdmin } from "services";

import { formatNumber, getGoogleKey, getVariance, useCancelAsyncOnUnmount } from "utils";

import { AdminHead, Button, Col, ErrorBoundary, MainPage, Row, TagNormal, Typography } from "components";

import { CalculatedSize } from "pages/admin/experiments/components/CalculatedSize";
import { DigifarmMap } from "pages/admin/experiments/components/DigifarmMap";
import { DrawPolygonMap } from "pages/admin/experiments/components/DrawPolygonMap";
import { FieldSizeSelect } from "pages/admin/experiments/components/FieldSizeSelect";
import { UserReportedMap } from "pages/admin/experiments/components/UserReportedMap";

export const FieldSizes = () => {
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [field, setField] = useState({
    size: 0,
  });
  const [location, setLocation] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [drawnArea, setDrawnArea] = useState(0);
  const [calculatedGeometry, setCalculatedGeometry] = useState(false);
  const [calculatedArea, setCalculatedArea] = useState(0);
  const [digifarmShape, setDigifarmShape] = useState(false);
  const [digifarmArea, setDigifarmArea] = useState(false);
  const [drawnPolygons, setDrawnPolygons] = useState([]);
  const [userGeometry, setUserGeometry] = useState(false);
  const [page, setPage] = useState(0);

  const signal = useCancelAsyncOnUnmount()();

  const loadAllFields = useCallback(() => {
    getAllFields({
      page,
      signal,
    })
      .then((res) => {
        setFields((fields) => [...fields, ...res.fields]);
        if (res.count > page * 250 && res.count > 250) {
          setPage(page + 1);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, signal]);

  const getFieldMarker = (field) => markers.find((item) => item.id === field.id);

  const convertEdgeToCoordinates = (width, height) => {
    // get canvas
    const canvas = document.getElementById("edgeMapCanvas");
    const ctx = canvas.getContext("2d");

    // get center point of image
    const center = {
      x: width / 2,
      y: height / 2,
    };

    // specify colors to look for
    const colors = {
      black: "0 0 0",
      white: "255 255 255",
    };

    const whitePixels = [];

    // create a series of lines in a radial pattern
    // where a is the angle from verticle
    // and r is the distance from center
    // then find the first r that hits a white pixel and push xy to array
    for (let a = 0; a <= 350; a += 10) {
      for (let r = 1; r <= center.x; r += 1) {
        const x = r * Math.sin((Math.PI * 2 * a) / 360);
        const y = r * Math.cos((Math.PI * 2 * a) / 360);

        // get pixel data and color at that point on image
        const pixel = ctx.getImageData(center.x + x, center.y - y, 1, 1);
        const pixelColor = `${pixel.data[0]} ${pixel.data[1]} ${pixel.data[2]}`;

        // if pixel is white and we don't already have a point for this angle, push to array of dots
        if (pixelColor === colors.white && !whitePixels.some((e) => e.a === a)) {
          whitePixels.push({
            a,
            r,
            center: {
              x: center.x,
              y: center.y,
            },
            x,
            y,
          });
        }
      }
    }

    // clear out any dots where the radius is more than v% larger/smaller than neighbouring pixels, to remove rando points
    const v = 40;
    const cleanPixels = [];
    whitePixels.forEach((pixel, i) => {
      if (
        whitePixels[i - 1] &&
        pixel.r <= whitePixels[i - 1].r * (1 + v / 100) &&
        pixel.r >= whitePixels[i - 1].r * (v / 100) &&
        whitePixels[i + 1] &&
        pixel.r <= whitePixels[i + 1].r * (1 + v / 100) &&
        pixel.r >= whitePixels[i + 1].r * (v / 100)
      ) {
        cleanPixels.push(pixel);
      } else if (!whitePixels[i - 1] || !whitePixels[i + 1]) {
        cleanPixels.push(pixel);
      }
    });

    const calculatedGeometry = {
      raw: cleanPixels,
      geometry: [],
    };
    setCalculatedGeometry(false);
    setCalculatedGeometry(calculatedGeometry);
    document.getElementById("edgeMapCanvas").remove();
  };

  const processEdgeDetection = (hybridMap, terrainMap, width, height) => {
    if (document.getElementById("edgeMapCanvas")) {
      document.getElementById("edgeMapCanvas").remove();
    }

    const edgeWorker = new Worker("/shp/edgeWorker.js");
    edgeWorker.postMessage({
      cmd: "appData",
      data: {
        width: width,
        height: height,
        ut: 0,
        lt: 0.1,
      },
    });

    // create a new canvas for the edge map
    const edgeMapCanvas = document.createElement("canvas");
    let edgeMapCTX = edgeMapCanvas.getContext("2d");
    edgeMapCanvas.id = "edgeMapCanvas";
    edgeMapCanvas.height = height;
    edgeMapCanvas.width = width;

    // create a new canvas for the overlay map
    const overlayMapCanvas = document.createElement("canvas");
    const overlayMapCTX = overlayMapCanvas.getContext("2d");
    overlayMapCanvas.id = "overlayMapCanvas";
    overlayMapCanvas.height = height;
    overlayMapCanvas.width = width;

    // use google maps static image and add to canvas once loaded
    const hybridImage = new Image();
    hybridImage.onload = function () {
      edgeMapCTX.filter = "grayscale(100%) brightness(110%) contrast(110%) saturate(110%) invert(100%)";
      edgeMapCTX.drawImage(this, 0, 0);

      // overlay terrain image for more accuracy
      const terrainImage = new Image();
      terrainImage.onload = function () {
        overlayMapCTX.drawImage(this, 0, 0);

        // replace grays in terrain map to transparents
        const terrainImageData = overlayMapCTX.getImageData(0, 0, width, height);
        for (let i = 0; i <= terrainImageData.data.length; i += 1) {
          if (
            terrainImageData.data[i + 3] &&
            terrainImageData.data[i] > 90 &&
            terrainImageData.data[i] < 110 &&
            terrainImageData.data[i + 1] > 90 &&
            terrainImageData.data[i + 1] < 110 &&
            terrainImageData.data[i + 2] > 90 &&
            terrainImageData.data[i + 2] < 110
          ) {
            terrainImageData.data[i + 3] = 0;
          } else if (
            terrainImageData.data[i + 3] &&
            terrainImageData.data[i] > 175 &&
            terrainImageData.data[i] < 195 &&
            terrainImageData.data[i + 1] > 220 &&
            terrainImageData.data[i + 1] < 240 &&
            terrainImageData.data[i + 2] > 175 &&
            terrainImageData.data[i + 2] < 195
          ) {
            terrainImageData.data[i + 3] = 0;
          }
        }

        const edgeImageData = edgeMapCTX.getImageData(0, 0, width, height).data;
        edgeMapCTX.filter = "none";
        edgeWorker.postMessage({
          cmd: "imgData",
          data: edgeImageData,
        });

        edgeMapCTX.putImageData(new ImageData(new Uint8ClampedArray(edgeImageData), width, height), 0, 0);

        edgeWorker.addEventListener(
          "message",
          (e) => {
            if (e.data.type === "gradientMagnitude") {
              // put edge detector image onto canvas
              edgeMapCTX = document.getElementById("edgeMapCanvas").getContext("2d");
              edgeMapCTX.putImageData(new ImageData(new Uint8ClampedArray(e.data.data), width, height), 0, 0);
              convertEdgeToCoordinates(width, height);
            }
          },
          false
        );
      };
      terrainImage.crossOrigin = "Anonymous";
      terrainImage.src = terrainMap;
    };

    hybridImage.crossOrigin = "Anonymous";
    hybridImage.src = hybridMap;
    document.getElementById("processing").appendChild(edgeMapCanvas);
  };

  const setStaticMap = (lng, lat) => {
    if (location && document.getElementById("staticMap")) {
      const width = document.getElementById("staticMap").offsetWidth;
      const height = document.getElementById("staticMap").offsetHeight;

      const URL = "https://maps.googleapis.com/maps/api/staticmap";
      const center = `${lat},${lng}`;
      const zoom = 14;
      const size = `${width}x${height}`;
      let styles = "&style=poi|element:labels|visibility:off";
      styles = `${styles}&style=feature:road.local|element:geometry.fill|color:white`;
      styles = `${styles}&style=feature:landscape|element:geometry.fill|color:black`;

      const hybridMap = `${URL}?center=${center}&zoom=${zoom}&size=${size}&maptype=hybrid${styles}&key=${getGoogleKey()}`;
      const terrainMap = `${URL}?center=${center}&zoom=${zoom}&size=${size}&maptype=terrain${styles}&key=${getGoogleKey()}`;

      processEdgeDetection(hybridMap, terrainMap, width, height);
    }
  };

  const updateField = (v) => {
    const field = fields[v];
    setDigifarmShape(false);
    setLocation(field.location);
    setField(field);
    setMarkers([
      {
        location: field.location,
        name: field.name,
        id: field.id,
        size: Math.sqrt((parseFloat(field.size) * 10000) / Math.PI),
      },
    ]);
    setStaticMap(field.location.lng, field.location.lat);

    getFieldShapeAdmin({
      fieldId: field.id,
      type: "shapeFile",
      signal,
    }).then((res) => {
      if (res.fieldShape) {
        setUserGeometry(res.fieldShape);
      }
    });

    getFieldShapeAdmin({
      fieldId: field.id,
      type: "digifarm",
      signal,
    })
      .then((res) => {
        if (res.fieldShape) {
          setDigifarmShape(res.fieldShape.geometry);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getMapOptions = (maps) => ({
    streetViewControl: false,
    keyboardShortcuts: false,
    scaleControl: true,
    fullscreenControl: false,
    rotateControl: false,
    styles: [
      {
        featureType: "poi.business",
        elementType: "labels",
        stylers: [
          {
            visibility: "off",
          },
        ],
      },
    ],
    gestureHandling: "cooperative",
    disableDoubleClickZoom: true,
    mapTypeControl: false,
    mapTypeId: maps.MapTypeId.HYBRID,
    mapTypeControlOptions: {
      style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: maps.ControlPosition.BOTTOM_CENTER,
      mapTypeIds: [maps.MapTypeId.ROADMAP, maps.MapTypeId.SATELLITE, maps.MapTypeId.HYBRID],
    },
    zoomControl: true,
    clickableIcons: false,
  });

  const setupDrawingAPI = (map, google) => {
    if (google.drawing) {
      const drawingManager = new google.drawing.DrawingManager({
        drawingMode: google.drawing.OverlayType.POLYGON,
        drawingControl: false,
        polygonOptions: {
          strokeColor: "red",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "red",
          fillOpacity: 0.2,
          clickable: true, // add this to edit drawing
          editable: true, // add this to edit drawing
          draggable: true, // add this to edit drawing
          geodesic: true, // add this to edit drawing
        },
      });

      google.event.addListener(drawingManager, "overlaycomplete", (e) => {
        if (e.type === google.drawing.OverlayType.POLYGON) {
          const shape = e.overlay;
          const area = google && google.geometry ? Math.round(google.geometry.spherical.computeArea(shape.getPath()) / 100) / 100 : 0;
          setDrawnArea(area);

          const coordinateArray = shape.getPath().getArray(); // add this to add polygon to state
          const geometry = coordinateArray.map((item) => {
            const lat = item.lat();
            const lng = item.lng();

            return { lat: lat, lng: lng };
          });
          setDrawnPolygons([geometry]); // add this for splitting field

          drawingManager.setDrawingMode(google.drawing.OverlayType.null); // add this to edit drawing

          google.event.addListener(shape, "mouseup", () => {
            // add this to update area after editing
            const area = google && google.geometry ? Math.round(google.geometry.spherical.computeArea(shape.getPath()) / 100) / 100 : 0;
            setDrawnArea(area);
          });

          // delete vertex by double clicking or right clicking
          google.event.addListener(shape, "rightclick", (d) => d.vertex && shape.getPath().removeAt(d.vertex));
          google.event.addListener(shape, "dblclick", (d) => d.vertex && shape.getPath().removeAt(d.vertex));
        }
      });

      drawingManager.setMap(map);

      if (drawnPolygons && drawnPolygons.length > 0) {
        // add this for splitting field
        drawingManager.setDrawingMode(google.drawing.OverlayType.null); // add this to edit drawing

        return drawnPolygons.map(
          (geometry) =>
            new google.Polygon({
              strokeColor: "red",
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: "red",
              fillOpacity: 0.2,
              paths: geometry,
              clickable: true,
              editable: true,
              draggable: true,
              geodesic: true,
              map,
            })
        );
      }
    }
  };

  const updateCalculatedMap = (v) => {
    const rawCoordinates = calculatedGeometry.raw;
    const { geometry } = calculatedGeometry;

    if (rawCoordinates.length > 0 && geometry.length <= 0) {
      const calculatedGeometry = {
        raw: rawCoordinates,
        geometry: [],
      };

      const { width } = v.size;
      const { height } = v.size;
      const degreeWidth = v.bounds.ne.lng - v.bounds.nw.lng;
      const degreeHeight = v.bounds.nw.lat - v.bounds.sw.lat;
      const pixelWidth = degreeWidth / width;
      const pixelHeight = degreeHeight / height;

      // calculate co-ordinates from x y
      rawCoordinates.forEach((pixel) => {
        calculatedGeometry.geometry.push({
          lng: v.bounds.nw.lng + (pixel.center.x + pixel.x) * pixelWidth,
          lat: v.bounds.sw.lat + (pixel.center.y + pixel.y) * pixelHeight,
        });
      });

      setCalculatedGeometry(false);
      setCalculatedGeometry(calculatedGeometry);
    }
  };

  const calculateFieldSize = (field) => {
    getDigifarmMapsGeo({
      lat: field.location.lat,
      lng: field.location.lng,
      fieldId: field.id,
      farmId: field.farmId,
      signal,
    })
      .then(() => {
        getFieldShapeAdmin({
          fieldId: field.id,
          type: "digifarm",
          signal,
        }).then((res) => {
          setDigifarmShape(res.fieldShape.geometry);
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (page > 0) {
      loadAllFields();
    }
  }, [loadAllFields, page]);

  useEffect(() => {
    loadAllFields();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline={
          <>
            Field sizes <TagNormal text="ALPHA" style={{ transform: "translate(0, -50%)", fontWeight: "bold" }} />
          </>
        }
        description="Choose a field to map below - you can draw out a shape on the draw map."
      />
      <div className="content">
        <ErrorBoundary>
          <FieldSizeSelect fields={fields} updateField={updateField} />
        </ErrorBoundary>

        <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]} className="margin-top-200">
          {/* prelint-ignore-col */}
          {location && (
            <ErrorBoundary>
              <UserReportedMap field={field} geometry={userGeometry} getMapOptions={getMapOptions} location={location} markers={markers} />
            </ErrorBoundary>
          )}
          {location && drawnPolygons && (
            <ErrorBoundary>
              <DrawPolygonMap
                drawnArea={drawnArea}
                field={field}
                getMapOptions={getMapOptions}
                location={location}
                markers={markers}
                setupDrawingAPI={setupDrawingAPI}
                polygons={drawnPolygons}
              />
            </ErrorBoundary>
          )}
        </Row>
        <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]} className="margin-top-200">
          {/* prelint-ignore-col */}
          {digifarmShape && field && (
            <ErrorBoundary>
              <DigifarmMap
                digifarmArea={digifarmArea}
                digifarmShape={digifarmShape}
                field={field}
                getFieldMarker={getFieldMarker}
                getMapOptions={getMapOptions}
                setDigifarmArea={setDigifarmArea}
              />
            </ErrorBoundary>
          )}
          {field && field.id && !digifarmShape && (
            <Col sm={24} md={12}>
              <Typography variant="h4">Digifarm polygon</Typography>
              <Typography variant="paragraph-m">
                {digifarmArea ? formatNumber({ number: digifarmArea, decimalPlaces: 4 }) : 0}
                ha ({digifarmArea ? getVariance(field.size, digifarmArea, 2) : 0}% variance)
              </Typography>
              <Button onClick={() => calculateFieldSize(field)} type="primary">
                Get DigiFarm shape
              </Button>
            </Col>
          )}
          {location && (
            <ErrorBoundary>
              <CalculatedSize
                calculatedArea={calculatedArea}
                calculatedGeometry={calculatedGeometry}
                field={field}
                getMapOptions={getMapOptions}
                location={location}
                markers={markers}
                setCalculatedArea={setCalculatedArea}
                updateCalculatedMap={updateCalculatedMap}
              />
            </ErrorBoundary>
          )}
        </Row>
        <div id="processing" />
      </div>
    </MainPage>
  );
};
